import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { Button } from '@components/ui/button/Button';
import { Checkbox } from '@shared/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@shared/components/ui/shadcn/dialog';
import { Input } from '@shared/components/ui/shadcn/input';
import { Label } from '@shared/components/ui/shadcn/label';
import { CreateCategoryPayload, ProductCategory, UpdateCategoryPayload } from '../../models/ProductCategory';

interface CategoryFormDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (payload: CreateCategoryPayload | UpdateCategoryPayload) => void;
  category?: ProductCategory | null;
  isLoading?: boolean;
}

const schema = z.object({
  name: z.string().trim().min(1).max(255),
  batch_required: z.boolean(),
});

type FormValues = z.infer<typeof schema>;

const DEFAULT_VALUES: FormValues = { name: '', batch_required: false };

export function CategoryFormDialog({ isOpen, onClose, onSubmit, category = null, isLoading }: CategoryFormDialogProps) {
  const { t } = useTranslation('productCategoriesModule');
  const isEditMode = category != null;

  const {
    control,
    register,
    handleSubmit,
    reset,
    formState: { isValid },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    mode: 'onChange',
    defaultValues: DEFAULT_VALUES,
  });

  useEffect(() => {
    if (!isOpen) {
      reset(DEFAULT_VALUES);
      return;
    }

    if (category) {
      reset({
        name: category.name,
        batch_required: category.batch_required,
      });
      return;
    }

    reset(DEFAULT_VALUES);
  }, [isOpen, category, reset]);

  const submit = handleSubmit((values) => {
    onSubmit(values);
  });

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{isEditMode ? t('formDialog.editTitle') : t('formDialog.createTitle')}</DialogTitle>
          <DialogDescription>
            {isEditMode ? t('formDialog.editDescription') : t('formDialog.createDescription')}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="category-name">{t('formDialog.nameLabel')}</Label>
            <Input
              id="category-name"
              placeholder={t('formDialog.namePlaceholder')}
              maxLength={255}
              {...register('name')}
              data-testid="category-name-input"
            />
          </div>

          <Controller
            control={control}
            name="batch_required"
            render={({ field }) => (
              <div className="flex items-start gap-3 rounded-md border border-border p-3">
                <Checkbox
                  id="category-batch-required"
                  checked={field.value}
                  onCheckedChange={(checked) => field.onChange(checked === true)}
                  data-testid="category-batch-required-checkbox"
                />
                <div className="space-y-1">
                  <Label htmlFor="category-batch-required" className="cursor-pointer">
                    {t('formDialog.batchRequiredLabel')}
                  </Label>
                  <p className="text-xs text-muted-foreground">{t('formDialog.batchRequiredHint')}</p>
                </div>
              </div>
            )}
          />

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={isLoading}>
              {t('formDialog.cancel')}
            </Button>
            <Button type="submit" variant="primary" disabled={!isValid || isLoading} data-testid="category-form-submit">
              {isEditMode ? t('formDialog.editSubmit') : t('formDialog.createSubmit')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
